import type { ChangeEvent } from 'react';
import { z } from 'zod';
import { isValidUsPhoneDigitCount, parsePhoneNumber } from './phoneUtils';

export const handlePhoneDigitsChange =
  (onChange: (value: string) => void) => (e: ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value.replace(/\D/g, ''));
  };

// `.refine` runs on the raw input before `.transform` normalises it, so
// validation relies on `isValidUsPhoneDigitCount` internally stripping the
// US country-code prefix (via `extractUsPhoneDigits`). Keep refine before
// transform: e.g. "11234567890" validates as 10 digits, then normalises to
// "1234567890".
export const optionalUsPhoneField = (fieldLabel: string) =>
  z
    .string()
    .refine(isValidUsPhoneDigitCount, {
      message: `${fieldLabel} must be exactly 10 digits`,
    })
    .transform((v) => {
      const trimmed = (v ?? '').trim();
      if (!trimmed) return undefined;
      return parsePhoneNumber(trimmed);
    })
    .optional();

// Same refine-before-transform dependency as `optionalUsPhoneField`: the
// digit-count check strips the country-code prefix internally, so the raw
// pre-transform value is what gets validated.
export const requiredUsPhoneField = () =>
  z
    .string()
    .refine((v) => (v ?? '').trim().length > 0, {
      message: 'Enter a phone number',
    })
    .refine((v) => isValidUsPhoneDigitCount(v), {
      message: 'Enter a valid 10-digit phone number',
    })
    .transform((v) => parsePhoneNumber(v.trim()));
